import React, { useRef } from 'react';
import cx from 'classnames';
import ClusterLayout from '../ClusterLayout';
import ClickArea from '../ClickArea';
import Text from '../Text';
import Tooltip from '../Tooltip';
import stylesheet from './DataWellGroup.css';
import PositiveArrow, { NegativeArrow } from './icons';

export type DataMetricDescriptorProps = {
  type?: 'positive' | 'negative' | 'link' | 'static';
  href?: string;
  children?: React.ReactNode;
};

export type DataLabelProps = {
  coin?: React.ReactNode;
  tooltip?: string;
  children?: React.ReactNode;
};

export type DataItemProps = {
  children?: React.ReactNode;
  onClick?: () => void;
  size?: 'small' | 'medium' | 'large';
  alignment?: 'vertical' | 'horizontal';
  className?: string;
};

export type DataWellGroupProps = {
  children?: React.ReactNode;
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
  className?: string;
};

const DataMetricDescriptor = ({
  type = 'static',
  href,
  children,
}: DataMetricDescriptorProps) => {
  let typeIcon: React.ReactNode;
  if (type && type === 'positive') {
    typeIcon = <PositiveArrow />;
  } else if (type && type === 'negative') {
    typeIcon = <NegativeArrow />;
  }
  return (
    <ClusterLayout gap={1} className={stylesheet.descriptor}>
      {typeIcon && typeIcon}
      {href ? (
        <a href={href} className={stylesheet.descriptorLink}>
          <Text appearance="tiny" className={stylesheet[type]}>
            {children}
          </Text>
        </a>
      ) : (
        <Text appearance="tiny" className={stylesheet[type]}>
          {children}
        </Text>
      )}
    </ClusterLayout>
  );
};

const DataLabel = ({ coin, tooltip, children }: DataLabelProps) => {
  const dataLabel = (
    <ClusterLayout gap={0} className={stylesheet.dataLabel}>
      {tooltip ? (
        <Tooltip direction="top" label={<Text>{tooltip}</Text>}>
          <ClusterLayout gap={coin ? 1 : 0}>
            {coin}
            {children}
          </ClusterLayout>
        </Tooltip>
      ) : (
        <ClusterLayout gap={coin ? 1 : 0}>
          {coin}
          {children}
        </ClusterLayout>
      )}
    </ClusterLayout>
  );

  return dataLabel;
};

const DataItem = ({
  children,
  onClick,
  size = 'medium',
  alignment = 'vertical',
  className,
}: DataItemProps) => {
  const targetRef = useRef<HTMLDivElement>(null);
  return onClick ? (
    <ClickArea targetRef={targetRef} onClick={onClick}>
      <div
        ref={targetRef}
        className={cx(
          [
            stylesheet.dataItem,
            stylesheet[size],
            stylesheet[alignment],
            className,
          ],
          {
            [stylesheet.dataItemInteractive]: onClick,
          },
        )}
      >
        {children}
      </div>
    </ClickArea>
  ) : (
    <div
      ref={targetRef}
      className={cx(
        [
          stylesheet.dataItem,
          stylesheet[size],
          stylesheet[alignment],
          className,
        ],
        {
          [stylesheet.dataItemInteractive]: onClick,
        },
      )}
    >
      {children}
    </div>
  );
};

const DataWellGroup = React.forwardRef<HTMLDivElement, DataWellGroupProps>(
  function DataWellGroup({ children, gap = 0, className }, forwardedRef) {
    return (
      <ClusterLayout
        gap={gap}
        ref={forwardedRef}
        className={cx(stylesheet.group, className)}
      >
        {children}
      </ClusterLayout>
    );
  },
);

export { DataWellGroup, DataItem, DataMetricDescriptor, DataLabel };
